import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import type { StylesConfig } from 'react-select';
import toast from 'react-hot-toast';
import Modal from '@components/common/Modal/Modal';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';
import applicationsService from '@/services/applications/applicationsService';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';

interface AssignStudentsToMentorModalProps {
    isOpen: boolean;
    onClose: () => void;
    mentorId: string;
    mentorName: string;
}

const customStyles: StylesConfig<any, true> = {
    control: (base, state) => ({
        ...base,
        minHeight: '41px',
        fontSize: '14px',
        borderRadius: '12px',
        borderColor: state.isFocused ? '#017DF2' : '#E2E8F0',
        backgroundColor: '#FAFCFF',
        cursor: 'pointer',
        boxShadow: state.isFocused ? '0 0 0 1px #017DF2' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#017DF2' : '#CBD5E1' },
        '@media (max-width: 1535px)': {
            minHeight: '36px',
            fontSize: '12px',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '4px 12px',
        flexWrap: 'wrap',
        gap: '4px',
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#E8F3FE',
        borderRadius: '12px',
        padding: '0 4px',
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#017DF2',
        fontSize: '12px',
        fontWeight: '600',
        padding: '2px 6px',
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#017DF2',
        borderRadius: '0 12px 12px 0',
        '&:hover': { backgroundColor: '#BDDFFB', color: '#0056b3' },
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    placeholder: (base) => ({
        ...base,
        color: '#94A3B8',
        fontSize: '14px',
        margin: 0,
        '@media (max-width: 1535px)': { fontSize: '12px' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 8px 0 0',
        color: '#94A3B8',
        '&:hover': { color: '#475569' },
        svg: {
            '@media (max-width: 1535px)': { width: '14px', height: '14px' },
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        overflow: 'hidden',
        zIndex: 9999,
        border: '1px solid #F1F5F9',
    }),
    menuList: (base) => ({ ...base, padding: '6px' }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        fontWeight: '500',
        color: state.isSelected ? '#ffffff' : '#1E293B',
        backgroundColor: state.isSelected ? '#017DF2' : state.isFocused ? '#F2F8FE' : 'transparent',
        borderRadius: '8px',
        padding: '8px 12px',
        cursor: 'pointer',
        '@media (max-width: 1535px)': { fontSize: '11px', padding: '6px 10px' },
    }),
    clearIndicator: (base) => ({ ...base, padding: '0 8px 0 0', color: '#94A3B8' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const AssignStudentsToMentorModal = ({ isOpen, onClose, mentorId, mentorName }: AssignStudentsToMentorModalProps) => {
    const queryClient = useQueryClient();
    const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

    // Fetch paid students with no mentor assigned yet
    const { data: eligibleResponse, isLoading, refetch } = useQuery({
        queryKey: ['eligible-students-for-mentor'],
        queryFn: () => applicationsService.adminListEligibleForMentor(),
        enabled: isOpen,
    });

    const eligibleStudents = useMemo(() => eligibleResponse?.data ?? [], [eligibleResponse]);

    const eligibleOptions = useMemo(() => {
        return eligibleStudents.map(student => {
            const cohortTitle = student.enrollments?.[0]?.cohort?.generated_cohort_title ?? 'No Cohort';
            return {
                value: student.id,
                label: `${student.full_name} (${student.email}) — ${cohortTitle}`,
            };
        });
    }, [eligibleStudents]);

    // Add Select All option
    const dropdownOptions = useMemo(() => {
        if (eligibleOptions.length === 0) return [];
        return [
            { value: 'select-all', label: 'Select All Eligible Students' },
            ...eligibleOptions,
        ];
    }, [eligibleOptions]);

    useEffect(() => {
        if (isOpen) {
            setSelectedOptions([]);
            void refetch();
        }
    }, [isOpen, refetch]);

    const assignMutation = useMutation({
        mutationFn: (appIds: string[]) => applicationsService.assignMentorBulk(mentorId, appIds),
        onSuccess: (res) => {
            const assignedCount = res.data?.filter((r: any) => r.success).length || 0;
            toast.success(`Successfully assigned ${assignedCount} students to ${mentorName}`);
            queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
            queryClient.invalidateQueries({ queryKey: ['mentors'] });
            onClose();
        },
        onError: (err) => {
            toast.error(getApiErrorMessage(err, 'Failed to assign students'));
        },
    });

    const handleSelectChange = (selected: any) => {
        const selectedArr = selected ? (selected as any[]) : [];
        const hasSelectAll = selectedArr.some(opt => opt.value === 'select-all');

        if (hasSelectAll) {
            // If already fully selected, toggle off (clear)
            if (selectedOptions.length === eligibleOptions.length) {
                setSelectedOptions([]);
            } else {
                // Else select all
                setSelectedOptions(eligibleOptions);
            }
        } else {
            setSelectedOptions(selectedArr);
        }
    };

    const handleAssign = () => {
        if (selectedOptions.length === 0) {
            toast.error('Please select at least one student to assign');
            return;
        }

        const appIds = selectedOptions.map(opt => opt.value);
        assignMutation.mutate(appIds);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Assign Students to ${mentorName}`}
            maxWidth="max-w-[580px]"
        >
            <div className="overflow-y-auto p-4 2xl:p-5 flex flex-col gap-5 text-sm text-[var(--secondary-color)] max-h-[60vh]">
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700">Select Students to Assign</label>
                    {isLoading ? (
                        <div className="text-xs text-[var(--disabled-text-color)] py-2">Loading paid students...</div>
                    ) : dropdownOptions.length === 0 ? (
                        <div className="text-xs text-[var(--semantic-error-color)] bg-red-50 border border-red-100 p-3 rounded-xl font-medium">
                            No paid students without an assigned mentor found.
                        </div>
                    ) : (
                        <Select
                            isMulti
                            options={dropdownOptions}
                            value={selectedOptions}
                            onChange={handleSelectChange}
                            placeholder="Search and select students..."
                            className="react-select-container animate-fade-in"
                            classNamePrefix="react-select"
                            closeMenuOnSelect={false}
                            styles={customStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    )}
                </div>

                {/* Premium Info Callout Block */}
                <div className="flex gap-3 text-xs bg-sky-50/70 border-l-4 border-sky-500 p-4 rounded-r-xl text-sky-800 backdrop-blur-sm shadow-sm">
                    <Icon name="infoCircle" size={18} className="text-sky-600 shrink-0 mt-0.5" />
                    <div>
                        <span className="font-semibold block mb-1 text-sky-900">Assignment Process Notes:</span>
                        Assigning students will link them to this mentor for 1-on-1 guidance and automatically send email notifications to both the student (sharing their mentor details) and the mentor (sharing student coordinates).
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3">
                <Button type="button" variant="dark" onClick={onClose} disabled={assignMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    onClick={handleAssign}
                    disabled={selectedOptions.length === 0 || assignMutation.isPending}
                >
                    {assignMutation.isPending
                        ? 'Assigning...'
                        : `Assign ${selectedOptions.length > 0 ? `(${selectedOptions.length})` : ''}`}
                </Button>
            </div>
        </Modal>
    );
};

export default AssignStudentsToMentorModal;
